import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Add auth token to all requests if available
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses (expired/invalid token)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for auth endpoints
      if (!error.config.url.includes('/auth/')) {
        localStorage.removeItem('auth_token');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Authentication
  adminLogin: (username, password) => axios.post(`${API}/auth/login`, { username, password }),
  ssoLogin: (accessToken) => axios.post(`${API}/auth/sso`, { access_token: accessToken }),
  checkAuth: () => axios.get(`${API}/auth/check`),
  getCurrentUser: () => axios.get(`${API}/auth/me`),
  logout: () => axios.post(`${API}/auth/logout`),

  // Legacy tickets (from CSV import)
  getTickets: (params) => axios.get(`${API}/tickets`, { params }),
  getTicketStats: (params) => axios.get(`${API}/tickets/stats`, { params }),
  getTicketFilters: () => axios.get(`${API}/tickets/filters`),
  getTicket: (id) => axios.get(`${API}/tickets/${id}`),
  uploadTickets: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/tickets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // AI Chat
  sendChat: (message, sessionId = "default") =>
    axios.post(`${API}/chat`, { message, session_id: sessionId }),
  getChatHistory: (sessionId = "default") =>
    axios.get(`${API}/chat/history`, { params: { session_id: sessionId } }),
  
  // FAN Reports
  createReport: (report) => axios.post(`${API}/reports`, report),
  getReports: (params) => axios.get(`${API}/reports`, { params }),
  getReport: (reportId) => axios.get(`${API}/reports/${reportId}`),
  updateReport: (reportId, update) => axios.put(`${API}/reports/${reportId}`, update),
  deleteReport: (reportId) => axios.delete(`${API}/reports/${reportId}`),

  // Managed Ticketing System
  getManagedTickets: (params) => axios.get(`${API}/ticketing/managed`, { params }),
  getNotifications: (sinceMinutes = 60) => axios.get(`${API}/ticketing/notifications`, { params: { since_minutes: sinceMinutes } }),
  getManagedTicket: (ticketId) => axios.get(`${API}/ticketing/managed/${ticketId}`),
  createManagedTicket: (ticket) => axios.post(`${API}/ticketing/managed`, ticket),
  updateManagedTicket: (ticketId, update) => axios.put(`${API}/ticketing/managed/${ticketId}`, update),
  deleteManagedTicket: (ticketId) => axios.delete(`${API}/ticketing/managed/${ticketId}`),
  addTicketNote: (ticketId, note) => axios.post(`${API}/ticketing/managed/${ticketId}/notes`, note),
  deleteTicketNote: (ticketId, noteId) => axios.delete(`${API}/ticketing/managed/${ticketId}/notes/${noteId}`),
  addTicketNoteWithAttachments: (ticketId, formData) => axios.post(
    `${API}/ticketing/managed/${ticketId}/notes-with-attachments`, 
    formData, 
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  uploadTicketAttachments: (ticketId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axios.post(`${API}/ticketing/managed/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  logTicketTime: (ticketId, entry) => axios.post(`${API}/ticketing/managed/${ticketId}/time`, entry),
  getManagedStats: (params) => axios.get(`${API}/ticketing/managed-stats`, { params }),
  getAnalytics: (days = 30) => axios.get(`${API}/ticketing/analytics`, { params: { days } }),
  analyzeTicketWithAI: (ticketId) => axios.post(`${API}/ticketing/managed/${ticketId}/analyze`),
  suggestResolutionNote: (ticketId) => axios.post(`${API}/ticketing/managed/${ticketId}/suggest-resolution`),
  
  // NEW AI Features
  getDraftResponse: (ticketId) => axios.post(`${API}/ticketing/managed/${ticketId}/draft-response`),
  checkDuplicates: (ticketId) => axios.post(`${API}/ticketing/managed/${ticketId}/check-duplicates`),
  getWeeklyReport: () => axios.get(`${API}/ticketing/ai/weekly-report`),
  
  // AI Ticket Quality Review
  reviewTicket: (ticketId) => axios.post(`${API}/ticketing/managed/${ticketId}/review`),
  getQualityStats: () => axios.get(`${API}/ticketing/ai/quality-stats`),
  
  // Approval Queue
  getApprovalQueue: (params) => axios.get(`${API}/ticketing/approval-queue`, { params }),
  approveTicket: (ticketId, action, notes, reviewer) => axios.post(
    `${API}/ticketing/managed/${ticketId}/approve`, 
    { action, notes, reviewer }
  ),
  
  // SLA Management
  getSlaDashboard: () => axios.get(`${API}/ticketing/sla/dashboard`),
  getTicketSlaStatus: (ticketId) => axios.get(`${API}/ticketing/sla/ticket/${ticketId}`),
  getSlaRules: () => axios.get(`${API}/ticketing/sla/rules`),
  updateSlaRules: (rules) => axios.put(`${API}/ticketing/sla/rules`, rules),
  
  // Ticketing Settings
  getTicketSettings: () => axios.get(`${API}/ticketing/settings`),
  updateTicketSettings: (settings) => axios.put(`${API}/ticketing/settings`, settings),
  
  // Email Integration
  pollEmails: () => axios.post(`${API}/ticketing/poll-emails`),
  getEmailStatus: () => axios.get(`${API}/ticketing/email-status`),
  
  // Azure AD Users
  getAzureUsers: () => axios.get(`${API}/ticketing/users`),
  searchUsers: (q) => axios.get(`${API}/ticketing/users/search`, { params: { q } }),
  refreshAzureUsers: () => axios.post(`${API}/ticketing/users/refresh`),
  
  // User Directory
  getUserDirectory: (params) => axios.get(`${API}/ticketing/users/directory`, { params }),
  getUserDetail: (email) => axios.get(`${API}/ticketing/users/directory/${encodeURIComponent(email)}`),
  updateUser: (email, data) => axios.put(`${API}/ticketing/users/directory/${encodeURIComponent(email)}`, data),
  createUser: (params) => axios.post(`${API}/ticketing/users/directory`, null, { params }),
  
  // Backup & Restore
  createBackup: () => axios.get(`${API}/ticketing/backup`),
  restoreBackup: (data) => axios.post(`${API}/ticketing/restore`, data),
  
  // Action1 RMM Integration (live data)
  getAction1Status: () => axios.get(`${API}/action1/status`),
  getAction1Endpoints: (params) => axios.get(`${API}/action1/endpoints`, { params }),
  getAction1Endpoint: (id) => axios.get(`${API}/action1/endpoints/${id}`),
  getTicketDevices: (ticketId) => axios.get(`${API}/action1/ticket-devices/${ticketId}`),
  getUserDevices: (params) => axios.get(`${API}/action1/user-devices`, { params }),
  
  // Asset Management (Database-First with Action1 Augmentation)
  getAssets: (params) => axios.get(`${API}/assets`, { params }),
  getAsset: (id) => axios.get(`${API}/assets/${id}`),
  createAsset: (data) => axios.post(`${API}/assets`, data),
  updateAsset: (id, data) => axios.put(`${API}/assets/${id}`, data),
  deleteAsset: (id, permanent = false) => axios.delete(`${API}/assets/${id}`, { params: { permanent } }),
  unretireAsset: (id) => axios.post(`${API}/assets/${id}/unretire`),
  bulkDeleteAssets: (assetIds, permanent = false) => axios.post(`${API}/assets/bulk-delete`, { asset_ids: assetIds, permanent }),
  getAssetStats: () => axios.get(`${API}/assets/stats`),
  getAssetHistory: (id) => axios.get(`${API}/assets/${id}/history`),
  
  // Asset Sync with Action1
  syncAssets: (fullSync = false) => axios.post(`${API}/assets/sync`, null, { params: { full_sync: fullSync } }),
  importAssetsFromAction1: () => axios.post(`${API}/assets/import`),
  getSyncStatus: () => axios.get(`${API}/assets/sync/status`),
  
  // Asset Label Printing
  getAssetLabelUrl: (id, size = 'thermal') => `${API}/assets/${id}/label?size=${size}`,
  
  // Asset Duplication
  duplicateAsset: (id, count = 1) => axios.post(`${API}/assets/${id}/duplicate`, null, { params: { count } }),
  
  // Asset Receipts
  uploadReceipt: (id, formData) => axios.post(`${API}/assets/${id}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAssetReceipts: (id) => axios.get(`${API}/assets/${id}/receipts`),
  getReceiptDownloadUrl: (assetId, receiptId) => `${API}/assets/${assetId}/receipts/${receiptId}/download`,
  deleteReceipt: (assetId, receiptId) => axios.delete(`${API}/assets/${assetId}/receipts/${receiptId}`),
  
  // Asset Check-In / Check-Out
  checkoutAsset: (id, data) => axios.post(`${API}/assets/${id}/checkout`, data),
  checkinAsset: (id, data) => axios.post(`${API}/assets/${id}/checkin`, data),
  
  // Legacy Asset Metadata (backwards compatibility)
  getAssetMetadata: () => axios.get(`${API}/action1/assets/metadata`),
  getAssetMeta: (id, endpointName) => axios.get(`${API}/action1/assets/${id}/meta`, { params: { endpoint_name: endpointName } }),
  updateAssetMeta: (id, data) => axios.put(`${API}/action1/assets/${id}/meta`, data),
  getAssetTickets: (id) => axios.get(`${API}/action1/assets/${id}/tickets`),
  linkTicketToAsset: (assetId, ticketId) => axios.post(`${API}/action1/assets/${assetId}/link-ticket/${ticketId}`),
  
  // External Purchases (Lenovo.com, Amazon, etc.)
  addExternalPurchase: (id, data) => axios.post(`${API}/action1/assets/${id}/external-purchase`, data),
  getAssetPurchases: (id) => axios.get(`${API}/action1/assets/${id}/purchases`),
  
  // Business Central Integration
  getBCStatus: () => axios.get(`${API}/bc/status`),
  getBCCompanies: () => axios.get(`${API}/bc/companies`),
  getBCInvoices: (companyId, params) => axios.get(`${API}/bc/invoices`, { params: { company_id: companyId, ...params } }),
  getBCInvoiceLines: (companyId, invoiceId) => axios.get(`${API}/bc/invoices/${invoiceId}/lines`, { params: { company_id: companyId } }),
  getBCVendors: (companyId, search) => axios.get(`${API}/bc/vendors`, { params: { company_id: companyId, search } }),
  getBCItems: (companyId, search) => axios.get(`${API}/bc/items`, { params: { company_id: companyId, search } }),
  linkInvoiceToAsset: (assetId, data) => axios.post(`${API}/bc/assets/${assetId}/link-invoice`, data),
  getAssetInvoices: (assetId) => axios.get(`${API}/bc/assets/${assetId}/invoices`),
  unlinkInvoiceFromAsset: (assetId, invoiceId) => axios.delete(`${API}/bc/assets/${assetId}/invoices/${invoiceId}`),
  
  // Service Surveys
  getSurveyStats: (weeks = 4) => axios.get(`${API}/surveys/stats`, { params: { weeks } }),
  getSurveyHistory: (page = 1, limit = 20) => axios.get(`${API}/surveys/history`, { params: { page, limit } }),
  sendSurveyBatch: (count = 5) => axios.post(`${API}/surveys/send-batch`, null, { params: { count } }),
  
  // UniFi Network
  getUniFiStatus: () => axios.get(`${API}/unifi/status`),
  configureUniFi: (host, username, password, apiToken, site = "default") => 
    axios.post(`${API}/unifi/configure`, null, { params: { host, username, password, api_token: apiToken, site } }),
  getUniFiDevices: () => axios.get(`${API}/unifi/devices`),
  getUniFiHealth: () => axios.get(`${API}/unifi/health`),
  getUniFiAlerts: (limit = 50) => axios.get(`${API}/unifi/alerts`, { params: { limit } }),
  getUniFiClients: () => axios.get(`${API}/unifi/clients`),
  pollUniFi: () => axios.post(`${API}/unifi/poll`),
  
  // Ticket Merge
  mergeTickets: (primaryTicketId, secondaryTicketIds) => axios.post(`${API}/ticketing/managed/merge`, {
    primary_ticket_id: primaryTicketId,
    secondary_ticket_ids: secondaryTicketIds,
  }),

  // Knowledge Base
  generateKBArticle: (ticketId) => axios.post(`${API}/kb/generate/${ticketId}`),
  getKBArticles: (params) => axios.get(`${API}/kb/articles`, { params }),
  getKBArticle: (kbId) => axios.get(`${API}/kb/articles/${kbId}`),
  updateKBArticle: (kbId, data) => axios.put(`${API}/kb/articles/${kbId}`, data),
  deleteKBArticle: (kbId) => axios.delete(`${API}/kb/articles/${kbId}`),
  getKBDownloadUrl: (kbId) => `${API}/kb/articles/${kbId}/download`,
  uploadKBToSharePoint: (kbId) => axios.post(`${API}/kb/articles/${kbId}/upload-sharepoint`),
  suggestKBArticles: (ticketId) => axios.get(`${API}/kb/suggest/${ticketId}`),
  smartSearchKB: (query) => axios.post(`${API}/kb/smart-search`, { query }),
  submitKBFeedback: (kbId, ticketId, helpful) => axios.post(`${API}/kb/articles/${kbId}/feedback`, { ticket_id: ticketId, helpful }),
  getKBFeedback: (kbId, ticketId) => axios.get(`${API}/kb/articles/${kbId}/feedback`, { params: { ticket_id: ticketId } }),
  uploadKBArticle: (formData) => axios.post(`${API}/kb/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Onboarding

  // Portal
  portalLogin: (email, password) => axios.post(`${API}/portal/auth/login`, { email, password }),
  portalRegister: (email, password, full_name) => axios.post(`${API}/portal/auth/register`, { email, password, full_name }),
  portalSSO: (accessToken) => axios.post(`${API}/portal/auth/sso`, { access_token: accessToken }),
  portalAuthConfig: () => axios.get(`${API}/portal/auth/config`),
  portalGetMyDevices: () => axios.get(`${API}/portal/my-devices`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('portal_token')}` },
  }),
  portalGetTickets: (params) => axios.get(`${API}/portal/tickets`, {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem('portal_token')}` },
  }),
  portalGetTicket: (ticketId) => axios.get(`${API}/portal/tickets/${ticketId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('portal_token')}` },
  }),
  portalCreateTicket: (data) => axios.post(`${API}/portal/tickets`, data, {
    headers: { Authorization: `Bearer ${localStorage.getItem('portal_token')}` },
  }),
  portalReply: (ticketId, content) => axios.post(`${API}/portal/tickets/${ticketId}/reply`, { content }, {
    headers: { Authorization: `Bearer ${localStorage.getItem('portal_token')}` },
  }),
  getOnboardingTickets: (limit = 20) => axios.get(`${API}/onboarding/tickets`, { params: { limit } }),
  getOnboardingTicket: (ticketId) => axios.get(`${API}/onboarding/tickets/${ticketId}`),
  getOnboardingConfig: () => axios.get(`${API}/onboarding/config`),
  triggerOnboardingPoll: () => axios.post(`${API}/onboarding/poll`),
  processOnboarding: (request) => axios.post(`${API}/onboarding/process`, request),
  parseTicketForOnboarding: (ticketId) => axios.post(`${API}/onboarding/parse-ticket/${ticketId}`),
  processOnboardingFromTicket: (ticketId, overrides) => axios.post(`${API}/onboarding/process-from-ticket/${ticketId}`, overrides),
  resetOnboardingClient: () => axios.post(`${API}/onboarding/reset-client`),
  getPhonePool: (status) => axios.get(`${API}/onboarding/phone-pool`, { params: status ? { status } : {} }),
  addPhoneNumbers: (numbers) => axios.post(`${API}/onboarding/phone-pool/add`, numbers),
  releasePhoneNumber: (number) => axios.post(`${API}/onboarding/phone-pool/release/${encodeURIComponent(number)}`),
  removePhoneNumber: (number) => axios.delete(`${API}/onboarding/phone-pool/${encodeURIComponent(number)}`),
  rerunOnboarding: (ticketId, steps = null) => axios.post(`${API}/onboarding/tickets/${ticketId}/rerun`, { steps }),
};

export default api;
